import React, { useCallback, useEffect } from 'react';
import { useQueryClient } from '@common-fe/common-fe';
import { Box } from '@common-fe/common-fe';

import ROUTES from '@/common/routes';
import { API_URL } from '@/dtos';
import { useHistory } from '@/modules/core/hooks';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';
import useLockboxesQuery from '@/modules/employer/components/Lockbox/queries/useLockboxes.query';
import useNavigationByList from '@/utils/hooks/useNavigationByList';

import { NavigationButton } from './LockboxNavigationButtons.styles';

interface Props {
  lockboxId: string;
}

const LockboxNavigationButtons: React.FC<Props> = ({ lockboxId }) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { setNextLockboxId, reset } = useLockboxDetailsStore();

  const {
    isItemFirstInList,
    isItemLastInList,
    prevItem,
    nextItem,
    params,
    isPageLoaded,
    handleUpdateLoadedPages,
  } = useNavigationByList({ currentItem: lockboxId });

  const { isLoading } = useLockboxesQuery({
    ...params,
    enabled: !isPageLoaded && (isItemFirstInList || isItemLastInList),
    onSuccess: handleUpdateLoadedPages,
  });

  useEffect(() => {
    if (nextItem) {
      setNextLockboxId(nextItem);
    }
  }, [setNextLockboxId, nextItem]);

  const handleNavigate = useCallback(
    (item: string) => {
      history.push(ROUTES.LOCKBOX_DETAILS(item));
      queryClient.removeQueries({
        queryKey: [API_URL.LOCKBOXES_BY_LOCKBOX_ID(lockboxId)],
      });
      reset();
    },
    [history, reset, lockboxId, queryClient]
  );

  return (
    <Box data-testid="lockbox-navigation-buttons" direction="row">
      <Box data-testid="prev-lockbox-navigation-button">
        <NavigationButton
          isBack
          {...(prevItem && !isLoading
            ? {
                onClick: () => handleNavigate(prevItem),
                isAvailable: true,
                color: 'iconPrimary',
              }
            : { color: 'iconSecondary' })}
        />
      </Box>
      <Box margin={{ left: 'spacing2' }} data-testid="next-lockbox-navigation-button">
        <NavigationButton
          {...(nextItem && !isLoading
            ? {
                onClick: () => handleNavigate(nextItem),
                isAvailable: true,
                color: 'iconPrimary',
              }
            : { color: 'iconSecondary' })}
        />
      </Box>
    </Box>
  );
};

export default LockboxNavigationButtons;
